import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Modal,
  Row,
  Col,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Button,
  Typography,
  Space,
} from "antd";
import { getHeaders } from "../../utility/getHeader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import fetchData from "../../functions/fetchData";
import { fetchComboData, fetchComboDataWithWhere } from "../../services/api";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const emptyForm = {
  iname: "",
  unit: "",
  quantity_type: "",
  tax: "",
  mrp: "",
  offerprice: "",
  category: "",
  subcat: "",
  description: "",
  isstockable: false,
  min_stock: "",
  item_code: "",
  barcode: "",
  brand: "",
  supplier_name: "",
  reorder_quantity: "",
  location: "",
  hsn_code: "",
  warranty_months: "",
  shelf_life_days: "",
  batch_tracking: false,
  serial_tracking: false,
};

const EditItemModal = ({ isOpen, item, onClose, onItemUpdated }) => {
  const [formdata, setFormData] = useState(emptyForm);

  const [getTax, setTax] = useState([]);
  const [getUnit, setUnits] = useState([]);
  const [getCategory, setCategory] = useState([]);
  const [getSubCategory, setSubCategory] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Populate form data when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        iname: item.iname || "",
        unit: item.unit || "",
        quantity_type: item.weight || "",
        tax: item.tax || "",
        mrp: item.mrp || "",
        offerprice: item.offerprice || "",
        category: item.catid || "",
        subcat: item.subcatid || "",
        description: item.description || "",
        isstockable: item.isstockable || false,
        min_stock: item.min_stock || "",
        item_code: item.item_code || "",
        barcode: item.barcode || "",
        brand: item.brand || "",
        supplier_name: item.supplier_name || "",
        reorder_quantity: item.reorder_quantity || "",
        location: item.location || "",
        hsn_code: item.hsn_code || "",
        warranty_months: item.warranty_months || "",
        shelf_life_days: item.shelf_life_days || "",
        batch_tracking: item.batch_tracking || false,
        serial_tracking: item.serial_tracking || false,
      });
    }
  }, [item]);

  // Fetch dropdown data when modal opens
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [taxes, units, categories] = await Promise.all([
          fetchData("taxes", null, "id", {}),
          fetchComboData("units", "name"),
          fetchComboData("categories", "name"),
        ]);

        setTax(taxes || []);
        setUnits(units || []);
        setCategory(categories || []);

        if (item && item.catid) {
          try {
            const subCats = await fetchComboDataWithWhere("subcategory", "subcat", {
              cat_id: item.catid
            });
            setSubCategory(subCats || []);
          } catch (error) {
            console.error("Error fetching subcategories for item:", error);
            setSubCategory([]);
          }
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen, item]);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (formdata.category) {
        try {
          const subCats = await fetchComboDataWithWhere("subcategory", "subcat", {
            cat_id: formdata.category
          });
          setSubCategory(subCats || []);
        } catch (error) {
          console.error("Error fetching subcategories:", error);
          setSubCategory([]);
        }
      } else {
        setSubCategory([]);
      }
    };

    if (isOpen && formdata.category) {
      fetchSubCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formdata.category, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(emptyForm);
      setSubCategory([]);
      setErrors({});
    }
  }, [isOpen]);

  const setField = (name, value) => {
    setFormData((prevData) => {
      if (name === "category") {
        return { ...prevData, category: value, subcat: "" };
      }
      return { ...prevData, [name]: value };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formdata.iname.trim()) newErrors.iname = "Item name is required";
    if (!formdata.unit) newErrors.unit = "Unit is required";
    if (!formdata.tax) newErrors.tax = "Tax is required";
    if (!formdata.mrp || formdata.mrp <= 0) newErrors.mrp = "Valid MRP is required";
    if (!formdata.offerprice || formdata.offerprice <= 0) newErrors.offerprice = "Valid offer price is required";
    if (!formdata.category) newErrors.category = "Category is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    setLoading(true);
    const requestBody = {
      iname: formdata.iname,
      unit: formdata.unit,
      weight: formdata.quantity_type,
      tax: formdata.tax,
      mrp: formdata.mrp,
      offerprice: formdata.offerprice,
      catid: formdata.category,
      subcatid: formdata.subcat,
      description: formdata.description,
      isstockable: formdata.isstockable,
      min_stock: formdata.min_stock,
      item_code: formdata.item_code,
      barcode: formdata.barcode,
      brand: formdata.brand,
      supplier_name: formdata.supplier_name,
      reorder_quantity: formdata.reorder_quantity,
      location: formdata.location,
      hsn_code: formdata.hsn_code,
      warranty_months: formdata.warranty_months,
      shelf_life_days: formdata.shelf_life_days,
      batch_tracking: formdata.batch_tracking,
      serial_tracking: formdata.serial_tracking,
    };
    const headers = getHeaders();
    try {
      const response = await axios.put(
        `/updatecommondata/items/id/${item.id}`,
        requestBody,
        headers
      );

      if (response.status === 200) {
        toast.success("Item updated successfully!");
        onItemUpdated();
        onClose();
      }
    } catch (error) {
      if (error.response) {
        console.error("Error updating item - response:", error.response);
      } else if (error.request) {
        console.error("Error updating item - request:", error.request);
      } else {
        console.error("Error updating item - message:", error.message);
      }
      toast.error("Failed to update item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyForm);
    setErrors({});
    onClose();
  };

  if (!item) return null;

  const colProps2 = { xs: 24, md: 12 };
  const colProps3 = { xs: 24, sm: 12, md: 8 };

  return (
    <Modal
      title="Edit Item"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      <ToastContainer position="top-right" autoClose={3000} />
      <form onSubmit={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Item Name</Text>
            <Input
              value={formdata.iname}
              status={errors.iname ? "error" : ""}
              onChange={(e) => setField("iname", e.target.value)}
            />
            {errors.iname && <Text type="danger" style={{ fontSize: 12 }}>{errors.iname}</Text>}
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Item Code</Text>
            <Input
              value={formdata.item_code}
              onChange={(e) => setField("item_code", e.target.value)}
            />
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Barcode</Text>
            <Input
              value={formdata.barcode}
              onChange={(e) => setField("barcode", e.target.value)}
            />
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Unit</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Unit"
              value={formdata.unit || undefined}
              status={errors.unit ? "error" : ""}
              onChange={(value) => setField("unit", value)}
            >
              {getUnit.map((unit) => (
                <Option key={unit.id} value={unit.name}>{unit.name}</Option>
              ))}
            </Select>
            {errors.unit && <Text type="danger" style={{ fontSize: 12 }}>{errors.unit}</Text>}
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Quantity Type</Text>
            <Input
              value={formdata.quantity_type}
              onChange={(e) => setField("quantity_type", e.target.value)}
            />
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Tax</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Tax"
              value={formdata.tax || undefined}
              status={errors.tax ? "error" : ""}
              onChange={(value) => setField("tax", value)}
            >
              {getTax.map((tax) => (
                <Option key={tax.id} value={tax.taxrate}>{tax.taxname} ({tax.taxrate}%)</Option>
              ))}
            </Select>
            {errors.tax && <Text type="danger" style={{ fontSize: 12 }}>{errors.tax}</Text>}
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>MRP</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={formdata.mrp}
              status={errors.mrp ? "error" : ""}
              onChange={(value) => setField("mrp", value)}
            />
            {errors.mrp && <Text type="danger" style={{ fontSize: 12 }}>{errors.mrp}</Text>}
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Offer Price</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={formdata.offerprice}
              status={errors.offerprice ? "error" : ""}
              onChange={(value) => setField("offerprice", value)}
            />
            {errors.offerprice && <Text type="danger" style={{ fontSize: 12 }}>{errors.offerprice}</Text>}
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Category</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Category"
              value={formdata.category || undefined}
              status={errors.category ? "error" : ""}
              onChange={(value) => setField("category", value)}
            >
              {getCategory.map((cat) => (
                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
              ))}
            </Select>
            {errors.category && <Text type="danger" style={{ fontSize: 12 }}>{errors.category}</Text>}
          </Col>

          <Col {...colProps2}>
            <Text style={{ fontSize: 13 }}>Sub Category</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Sub Category"
              value={formdata.subcat || undefined}
              onChange={(value) => setField("subcat", value)}
            >
              {getSubCategory.map((subcat) => (
                <Option key={subcat.id} value={subcat.id}>{subcat.subcat}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24}>
            <Text style={{ fontSize: 13 }}>Description</Text>
            <TextArea
              rows={3}
              value={formdata.description}
              placeholder="Enter description"
              onChange={(e) => setField("description", e.target.value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>Brand</Text>
            <Input
              value={formdata.brand}
              onChange={(e) => setField("brand", e.target.value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>Default Supplier Name</Text>
            <Input
              value={formdata.supplier_name}
              onChange={(e) => setField("supplier_name", e.target.value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>HSN/SAC Code</Text>
            <Input
              value={formdata.hsn_code}
              onChange={(e) => setField("hsn_code", e.target.value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>Location/Bin</Text>
            <Input
              value={formdata.location}
              onChange={(e) => setField("location", e.target.value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>Warranty (Months)</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={formdata.warranty_months}
              onChange={(value) => setField("warranty_months", value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>Shelf Life (Days)</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={formdata.shelf_life_days}
              onChange={(value) => setField("shelf_life_days", value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>Minimum Stock Level</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={formdata.min_stock}
              onChange={(value) => setField("min_stock", value)}
            />
          </Col>

          <Col {...colProps3}>
            <Text style={{ fontSize: 13 }}>Reorder Quantity</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={formdata.reorder_quantity}
              onChange={(value) => setField("reorder_quantity", value)}
            />
          </Col>

          <Col {...colProps2}>
            <Checkbox
              checked={formdata.isstockable}
              onChange={(e) => setField("isstockable", e.target.checked)}
            >
              Is Stockable
            </Checkbox>
          </Col>

          <Col {...colProps2}>
            <Checkbox
              checked={formdata.batch_tracking}
              onChange={(e) => setField("batch_tracking", e.target.checked)}
            >
              Enable Batch Tracking
            </Checkbox>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                For food/pharma items with expiry dates
              </Text>
            </div>
          </Col>

          <Col {...colProps2}>
            <Checkbox
              checked={formdata.serial_tracking}
              onChange={(e) => setField("serial_tracking", e.target.checked)}
            >
              Enable Serial Tracking
            </Checkbox>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                For electronics/high-value items
              </Text>
            </div>
          </Col>

          <Col xs={24} style={{ textAlign: 'right', marginTop: 8 }}>
            <Space>
              <Button onClick={handleClose} disabled={loading}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {loading ? "Updating..." : "Update Item"}
              </Button>
            </Space>
          </Col>
        </Row>
      </form>
    </Modal>
  );
};

export default EditItemModal;
